import type { SemesterKey } from '@/domains/shared';
import { useFilterStore } from '@/shared/stores';
import { useCallback } from 'react';

/**
 * Hook Fachada (Facade) para interactuar con los filtros globales de la aplicación.
 * Este es el ÚNICO lugar que debe importar y usar `useFilterStore` directamente.
 * Proporciona una API estable y segura para el resto de la aplicación.
 */
export const useAppFilters = () => {
  // 1. Selecciona cada pieza del estado de forma individual.
  // Esto es crucial para evitar re-renders innecesarios en los componentes que consumen el hook.
  const year = useFilterStore((state) => state.year);
  const semester = useFilterStore((state) => state.semester);
  const setGlobalFilters = useFilterStore((state) => state.setGlobalFilters);
  const resetFilters = useFilterStore((state) => state.resetFilters);

  // 2. Memoiza las funciones de actualización para mantener una referencia estable.
  const handleSetFilters = useCallback(
    (filters: { year: string; semester: SemesterKey }) => {
      setGlobalFilters(filters);
    },
    [setGlobalFilters],
  );

  const handleResetFilters = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  // 3. Devuelve una API pública y limpia para que la usen los componentes.
  return {
    // Valores de los filtros
    year,
    semester,

    // Acciones para modificar los filtros
    setFilters: handleSetFilters,
    resetFilters: handleResetFilters,
  };
};
